import type { BridgeMessage, MessageEnvelope, MessageKind, PayloadFor } from '../protocol/messages';
import { createMessage, PROTOCOL_VERSION } from '../protocol/messages';

/**
 * Client-side transports.
 *
 * The engine client runs in one of two places and must not care which:
 *
 * | Where the client runs | Transport | Host side |
 * | --------------------- | --------- | --------- |
 * | iframe embedded by the HtmlService sidebar | `postMessage` | `IframeHostTransport` |
 * | inside the `HtmlService` sandbox (no hosting configured) | `google.script.run` | `AppsScriptHostTransport` |
 *
 * Both carry the same validated envelopes, so the controller, the engine and
 * the settings UI are identical in both modes. The transport is chosen from
 * `bootstrap.transport`, which the host injects.
 */

export interface ClientTransport {
  readonly name: string;
  /** Send a message to the host. Never throws. */
  send(message: BridgeMessage): void;
  /** Begin listening for host messages. Returns a teardown function. */
  listen(handler: (value: unknown, origin?: string) => void): () => void;
}

/** The subset of `window` this module needs (keeps it testable in jsdom). */
export interface ClientWindowLike {
  postMessage?(message: unknown, targetOrigin: string): void;
  addEventListener?(type: 'message', listener: (event: MessageEvent) => void): void;
  removeEventListener?(type: 'message', listener: (event: MessageEvent) => void): void;
  parent?: ClientWindowLike;
}

export interface ClientBootstrap {
  /** Channel token minted by the host and injected into the page. */
  channel: string;
  /** Which transport the host expects. */
  transport: 'iframe' | 'sandbox' | 'direct';
  /** Engine URL when the host embedded an iframe (informational). */
  engineUrl: string | null;
  /** Client build id, reported in `client:ready`. */
  clientVersion: string;
}

/** `document.currentScript`-style bootstrap lookup, tolerant of older browsers. */
export interface BootstrapResult {
  bootstrap: ClientBootstrap;
  /** Non-fatal configuration problems, surfaced in the sidebar. */
  problems: string[];
}

const FALLBACK_CHANNEL = 'chartforge-missing-channel';

/**
 * Read the host-injected bootstrap config.
 *
 * `window.__CHARTFORGE__` is written by the host page *before* the engine
 * bundle loads. A missing or malformed value is not fatal: the client falls
 * back to the least-trusted transport, reports the problem, and the bridge will
 * reject every message until a valid channel arrives — a misconfigured host
 * must never silently degrade into an unvalidated bridge.
 */
export function readBootstrap(source?: unknown): BootstrapResult {
  const problems: string[] = [];
  const raw =
    source ??
    (typeof globalThis !== 'undefined'
      ? (globalThis as Record<string, unknown>).__CHARTFORGE__
      : undefined);

  if (!raw || typeof raw !== 'object') {
    problems.push('Missing window.__CHARTFORGE__ bootstrap configuration from the host.');
    return {
      bootstrap: {
        channel: FALLBACK_CHANNEL,
        transport: 'direct',
        engineUrl: null,
        clientVersion: PROTOCOL_VERSION.toString()
      },
      problems
    };
  }

  const candidate = raw as Partial<ClientBootstrap>;
  const channel = typeof candidate.channel === 'string' ? candidate.channel : '';
  if (!channel) {
    problems.push('Bootstrap config has no channel token; the host bridge will reject messages.');
  }
  const transport =
    candidate.transport === 'iframe' ||
    candidate.transport === 'sandbox' ||
    candidate.transport === 'direct'
      ? candidate.transport
      : 'direct';
  const engineUrl = typeof candidate.engineUrl === 'string' ? candidate.engineUrl : null;
  const clientVersion =
    typeof candidate.clientVersion === 'string' ? candidate.clientVersion : 'dev';

  return {
    bootstrap: { channel: channel || FALLBACK_CHANNEL, transport, engineUrl, clientVersion },
    problems
  };
}

export interface PostMessageClientTransportOptions {
  /** The client's own `window` (to listen) — defaults to the global window. */
  window?: ClientWindowLike;
  /** The window host messages are posted to — defaults to `window.parent`. */
  target?: ClientWindowLike;
  /**
   * Target origin used when posting. The Apps Script sidebar frame has an
   * opaque origin, so `'*'` is required there; a future externally hosted
   * sidebar should pass its real origin.
   */
  targetOrigin?: string;
}

/** `window.postMessage` transport (iframe mode). */
export class PostMessageClientTransport implements ClientTransport {
  readonly name = 'post-message';

  private readonly window: ClientWindowLike | undefined;
  private readonly explicitTarget: ClientWindowLike | undefined;
  private readonly targetOrigin: string;

  constructor(options: PostMessageClientTransportOptions = {}) {
    this.window =
      options.window ?? (typeof window !== 'undefined' ? (window as ClientWindowLike) : undefined);
    this.explicitTarget = options.target;
    this.targetOrigin = options.targetOrigin ?? '*';
  }

  private get target(): ClientWindowLike | undefined {
    return this.explicitTarget ?? this.window?.parent ?? this.window;
  }

  send(message: BridgeMessage): void {
    const target = this.target;
    if (!target || typeof target.postMessage !== 'function') {
      return;
    }
    try {
      target.postMessage(message, this.targetOrigin);
    } catch {
      // A cross-origin/cross-process failure must not break the sidebar: the
      // next user action will try again.
    }
  }

  listen(handler: (value: unknown, origin?: string) => void): () => void {
    const source = this.window;
    if (!source?.addEventListener) {
      return () => undefined;
    }
    const listener = (event: MessageEvent): void => {
      handler((event as MessageEvent).data, (event as MessageEvent).origin);
    };
    source.addEventListener('message', listener);
    return () => {
      source.removeEventListener?.('message', listener);
    };
  }
}

/**
 * The `google.script.run` surface the sandbox exposes.
 *
 * Only the three members this transport uses are declared: Apps Script's proxy
 * returns itself from the handler setters, and the server function is reached
 * through the index signature so the *function name* stays a documented
 * constant (`cfBridgeSend`) rather than a silent typo.
 */
export interface GoogleScriptRunLike {
  withSuccessHandler(handler: (result: unknown) => void): GoogleScriptRunLike;
  withFailureHandler(handler: (error: unknown) => void): GoogleScriptRunLike;
  [serverFunction: string]: unknown;
}

export interface SandboxClientTransportOptions {
  /** Injected for tests; defaults to `globalThis.google.script.run`. */
  runner?: GoogleScriptRunLike;
  /** Poll interval for queued host messages, in ms. */
  pollIntervalMs?: number;
  /** How fast to poll while a user action is in flight, in ms. */
  fastPollIntervalMs?: number;
  onError?: (error: unknown) => void;
  /** Injectable timer seam for tests. */
  setTimer?: (handler: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

/** Server functions the sandbox client talks to. */
export const BRIDGE_SEND_FUNCTION = 'cfBridgeSend';
export const BRIDGE_POLL_FUNCTION = 'cfBridgePoll';

export const DEFAULT_POLL_INTERVAL_MS = 1_000;
export const DEFAULT_FAST_POLL_INTERVAL_MS = 180;

/**
 * `google.script.run` transport (sandbox mode).
 *
 * Apps Script's sandbox exposes `google.script.run` but no `window.parent`
 * messaging. Outbound messages are forwarded to the server function
 * `cfBridgeSend`, and host replies are *pulled* from `cfBridgePoll` -- an
 * Apps Script sidebar page is rendered once and cannot push into the client
 * afterwards, so polling is the only correct direction for that path.
 *
 * Both directions carry the exact same envelopes as the iframe path, so the
 * bridge, the handlers and the parser are shared.
 */
export class GoogleScriptClientTransport implements ClientTransport {
  readonly name = 'google-script-run';

  private handler: ((value: unknown, origin?: string) => void) | null = null;
  private timer: unknown = null;
  private polling = false;
  private stopped = true;

  constructor(private readonly options: SandboxClientTransportOptions = {}) {}

  private get runner(): GoogleScriptRunLike | undefined {
    if (this.options.runner) {
      return this.options.runner;
    }
    const candidate = (globalThis as Record<string, unknown>).google as
      { script?: { run?: GoogleScriptRunLike } } | undefined;
    return candidate?.script?.run;
  }

  private get setTimer(): (handler: () => void, ms: number) => unknown {
    return this.options.setTimer ?? ((handler, ms) => setTimeout(handler, ms));
  }

  private get clearTimer(): (handle: unknown) => void {
    return this.options.clearTimer ?? ((handle) => clearTimeout(handle as never));
  }

  send(message: BridgeMessage): void {
    const runner = this.runner;
    if (!runner) {
      this.options.onError?.(new Error('google.script.run is not available in this context'));
      return;
    }
    try {
      const call = runner
        .withSuccessHandler(() => this.schedulePoll(this.fastInterval))
        .withFailureHandler((error: unknown) => this.options.onError?.(error));
      (call[BRIDGE_SEND_FUNCTION] as (payload: string) => void)(JSON.stringify(message));
    } catch (error) {
      this.options.onError?.(error);
    }
  }

  listen(handler: (value: unknown, origin?: string) => void): () => void {
    this.handler = handler;
    this.stopped = false;
    // Poll once immediately so a freshly loaded client collects the host's
    // reply to `client:ready` without waiting a full interval.
    this.poll();
    return () => {
      this.stopped = true;
      this.handler = null;
      if (this.timer !== null) {
        this.clearTimer(this.timer);
        this.timer = null;
      }
    };
  }

  /** Fetch queued host messages and feed them to the parser. */
  poll(): void {
    const runner = this.runner;
    if (!runner || this.polling || this.stopped) {
      return;
    }
    this.polling = true;
    try {
      const call = runner
        .withSuccessHandler((result: unknown) => {
          this.polling = false;
          this.consume(result);
          this.schedulePoll(this.interval);
        })
        .withFailureHandler((error: unknown) => {
          this.polling = false;
          this.options.onError?.(error);
          // Back off but keep trying: a transient failure must not strand the
          // sidebar, and the next successful poll delivers everything queued.
          this.schedulePoll(this.interval);
        });
      (call[BRIDGE_POLL_FUNCTION] as () => void)();
    } catch (error) {
      this.polling = false;
      this.options.onError?.(error);
      this.schedulePoll(this.interval);
    }
  }

  /** Handle a polled payload: a single envelope or an array of them. */
  consume(payload: unknown): void {
    if (payload === null || payload === undefined) {
      return;
    }
    const values = Array.isArray(payload) ? payload : [payload];
    for (const value of values) {
      this.deliver(value);
    }
  }

  /** Feed one host envelope into the client (also used directly by tests). */
  deliver(payload: unknown): void {
    if (!this.handler) {
      return;
    }
    // `google.script.run` passes objects across a serialization boundary, but a
    // host may also send a JSON string; support both without trusting either.
    let value: unknown = payload;
    if (typeof payload === 'string') {
      try {
        value = JSON.parse(payload);
      } catch {
        value = payload;
      }
    }
    this.handler(value, undefined);
  }

  private get interval(): number {
    return this.options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  }

  private get fastInterval(): number {
    return this.options.fastPollIntervalMs ?? DEFAULT_FAST_POLL_INTERVAL_MS;
  }

  private schedulePoll(ms: number): void {
    if (this.stopped) {
      return;
    }
    if (this.timer !== null) {
      this.clearTimer(this.timer);
    }
    this.timer = this.setTimer(() => {
      this.timer = null;
      this.poll();
    }, ms);
  }
}

/** Choose the transport the host asked for. */
export function createClientTransport(
  bootstrap: ClientBootstrap,
  overrides: {
    window?: ClientWindowLike;
    target?: ClientWindowLike;
    targetOrigin?: string;
    runner?: GoogleScriptRunLike;
  } = {}
): ClientTransport {
  if (bootstrap.transport === 'sandbox') {
    return new GoogleScriptClientTransport(overrides.runner ? { runner: overrides.runner } : {});
  }
  return new PostMessageClientTransport({
    ...(overrides.window ? { window: overrides.window } : {}),
    ...(overrides.target ? { target: overrides.target } : {}),
    ...(overrides.targetOrigin ? { targetOrigin: overrides.targetOrigin } : {})
  });
}

/** Build an outbound envelope for the host (used by the controller). */
export function outbound<K extends MessageKind>(
  kind: K,
  payload: PayloadFor<K>,
  channel: string,
  requestId?: string
): MessageEnvelope<K> {
  return createMessage(kind, payload, {
    channel,
    ...(requestId !== undefined ? { requestId } : {})
  });
}
